import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Layouts/Header';
import Footer from '../Layouts/Footer';
import {
  FiMapPin, FiTruck, FiCheckSquare, FiPackage,
  FiClock, FiChevronDown, FiWifi, FiAlertCircle,
  FiNavigation, FiGrid, FiActivity
} from 'react-icons/fi';

/* ── SVG Pulse Radar ── */
const PulseRadar = () => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none z-0">
    {[1, 2, 3, 4].map(i => (
      <motion.div key={i} className="absolute inset-0 rounded-full border border-sky-400/20"
        initial={{ scale: 0.3, opacity: 0.8 }}
        animate={{ scale: 1.4, opacity: 0 }}
        transition={{ duration: 4, delay: i * 1, repeat: Infinity, ease: 'easeOut' }}>
      </motion.div>
    ))}
    {/* Cross-hair lines */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-full h-px bg-sky-400/10"></div>
    </div>
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="h-full w-px bg-sky-400/10"></div>
    </div>
    <div className="absolute inset-0 rounded-full border border-sky-400/10"></div>
    <div className="absolute inset-[25%] rounded-full border border-sky-400/10"></div>
    <div className="absolute inset-[45%] rounded-full border border-sky-400/8"></div>
    {/* Blip dots */}
    {[
      { top: '25%', left: '60%' },
      { top: '55%', left: '30%' },
      { top: '70%', left: '65%' },
      { top: '35%', left: '40%' },
    ].map((pos, i) => (
      <motion.div key={i} className="absolute w-2 h-2 bg-sky-400 rounded-full"
        style={{ top: pos.top, left: pos.left }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 2, delay: i * 0.5, repeat: Infinity }}>
      </motion.div>
    ))}
  </div>
);

/* ── Shipment Journey Timeline ── */
const ShipmentTimeline = () => {
  const steps = [
    { icon: <FiPackage />, label: 'Picked Up', time: '09:00 AM', loc: 'Mumbai Warehouse', done: true },
    { icon: <FiGrid />, label: 'Sorted', time: '10:30 AM', loc: 'BKC Hub', done: true },
    { icon: <FiTruck />, label: 'In Transit', time: '01:15 PM', loc: 'NH-48, Pune', done: true, active: true },
    { icon: <FiNavigation />, label: 'Out for Delivery', time: 'Estimated 5 PM', loc: 'Pune City', done: false },
    { icon: <FiCheckSquare />, label: 'Delivered', time: 'Pending', loc: 'Customer Address', done: false },
  ];
  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex items-start gap-0 min-w-max mx-auto">
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center w-44 text-center">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl mb-3 transition-all ${
                s.active ? 'bg-sky-400 text-black shadow-[0_0_20px_rgba(56,189,248,0.5)] scale-110' :
                s.done ? 'bg-sky-400/20 text-sky-400 border border-sky-400/40' :
                'bg-gray-800 text-gray-500 border border-gray-700'
              }`}>
                {s.icon}
              </div>
              {s.active && (
                <motion.div className="w-2 h-2 bg-sky-400 rounded-full mb-2 -mt-1"
                  animate={{ opacity: [1, 0, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
                </motion.div>
              )}
              <p className={`font-medium text-sm mb-1 ${s.active ? 'text-sky-400' : s.done ? 'text-gray-300' : 'text-gray-600'}`}>{s.label}</p>
              <p className="text-xs text-gray-500">{s.time}</p>
              <p className="text-xs text-gray-600 mt-0.5">{s.loc}</p>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-px w-16 mt-7 mx-1 ${i < 2 ? 'bg-sky-400/50' : 'bg-gray-700 border-dashed'}`}
                style={{ borderTop: i >= 2 ? '1px dashed rgba(55,65,81,0.8)' : 'none' }}>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/* ── Capability Tabs ── */
const capTabs = {
  tracking: {
    icon: <FiMapPin />, label: 'Live Tracking',
    heading: 'Know where every shipment is. Always.',
    body: 'Full-fleet real-time GPS tracking on a live map. Share a branded tracking link with customers — zero WISMO (Where Is My Order) calls. Supports 2-wheelers, trucks, and inter-city freight.',
    chips: ['GPS Polling Every 10s', 'Customer Tracking Link', 'Geo-fence Alerts', 'ETA Prediction'],
    img: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=700&q=80',
  },
  route: {
    icon: <FiNavigation />, label: 'Route Optimisation',
    heading: 'Cut fuel costs by 20%. Every day.',
    body: 'Our AI analyses live traffic, road conditions, delivery time windows, and vehicle load capacity to generate the optimal multi-stop route. Integrates with Google Maps and HERE Maps.',
    chips: ['Multi-stop Optimisation', 'Live Traffic Integration', 'Vehicle Load Planning', 'Fuel Cost Reporting'],
    img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=700&q=80',
  },
  pod: {
    icon: <FiCheckSquare />, label: 'Proof of Delivery',
    heading: 'Signed, sealed, delivered. Digitally.',
    body: 'Drivers capture e-signatures, OTP verification, and delivery photos from the app — fully timestamped and geo-tagged. Instantly available in your dashboard and shareable with clients.',
    chips: ['E-Signature Capture', 'OTP Delivery Confirm', 'Photo Evidence', 'WhatsApp POD Share'],
    img: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=700&q=80',
  },
  analytics: {
    icon: <FiActivity />, label: 'Analytics',
    heading: 'Data that drives better operations.',
    body: 'Cohort-level on-time delivery rates, driver performance scorecards, hub-wise throughput, customer SLA breaches, and fuel efficiency trends — all in one live command centre.',
    chips: ['Driver Scorecards', 'SLA Breach Alerts', 'Hub Performance', 'Fuel Efficiency Index'],
    img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80',
  },
};

/* ── FAQ ── */
const Faq = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} className={`border rounded-2xl overflow-hidden transition-all ${open === i ? 'border-sky-500/40 bg-sky-500/5' : 'border-gray-800 bg-gray-900'}`}>
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
            <span className={`font-medium ${open === i ? 'text-sky-400' : 'text-gray-200'}`}>{f.q}</span>
            <FiChevronDown className={`transition-transform duration-300 ${open === i ? 'rotate-180 text-sky-400' : 'text-gray-600'}`} />
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <p className="p-5 pt-2 text-gray-400 border-t border-gray-800">{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════ MAIN PAGE ═══════════════════════════════════════ */
export default function LogisticsManagement() {
  useEffect(() => window.scrollTo(0, 0), []);
  const [activeTab, setActiveTab] = useState('tracking');

  const faqs = [
    { q: 'Can I manage multi-city deliveries from one dashboard?', a: 'Yes — plan and dispatch routes across unlimited cities and hubs. Our route engine handles inter-city and intra-city deliveries from a single unified view.' },
    { q: 'Does it integrate with my existing ERP or WMS?', a: 'Yes, we provide REST APIs and pre-built connectors for SAP, Oracle, Microsoft Dynamics, and major warehouse management systems.' },
    { q: 'How does driver tracking work?', a: 'Drivers install our Android app (2 MB, low battery usage). GPS pings every 10 seconds and the live location appears on your dashboard map in real-time.' },
    { q: 'Is there a customer-facing tracking page?', a: 'Yes — white-label the tracking page with your brand logo and colors. Customers receive an SMS/WhatsApp link automatically at dispatch.' },
  ];

  const kpis = [
    { icon: <FiTruck />, label: 'Deliveries Today', value: '1,284', delta: '+47 vs yesterday', status: 'up' },
    { icon: <FiClock />, label: 'On-Time Rate', value: '97.3%', delta: '+1.2%', status: 'up' },
    { icon: <FiAlertCircle />, label: 'Delayed Shipments', value: '23', delta: '−8 from avg', status: 'down' },
    { icon: <FiActivity />, label: 'Active Fleet', value: '184', delta: '12 idle', status: 'neutral' },
  ];

  const tab = capTabs[activeTab];

  return (
    <div className="min-h-screen bg-[#0d1117] font-sans text-white">
      <div className="bg-black border-b border-gray-900"><Header /></div>

      {/* ── HERO: Radar Pulse ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32">
        <PulseRadar />
        {/* Radial dark vignette */}
        <div className="absolute inset-0 bg-radial-at-center from-transparent to-[#0d1117] pointer-events-none z-10"
          style={{ background: 'radial-gradient(ellipse at center, transparent 30%, #0d1117 80%)' }}></div>

        <div className="relative z-20 text-center max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="flex justify-center items-center gap-3 mb-8">
              <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse"></span>
              <span className="text-sky-400 text-xs font-medium uppercase tracking-[0.3em]">Live Operations Platform</span>
              <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse"></span>
            </div>
            <h1 className="text-7xl lg:text-9xl font-medium leading-none tracking-tighter mb-6">
              LOGISTICS<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-blue-400 to-sky-300">IN CONTROL.</span>
            </h1>
            <p className="text-gray-400 text-xl max-w-2xl mx-auto mb-12 leading-relaxed">
              Real-time fleet tracking, AI route optimisation, and digital proof of delivery — built for logistics teams that never sleep.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/products#DemoForm">
                <button className="bg-sky-500 hover:bg-sky-600 text-white font-medium px-10 py-4 rounded-xl text-lg transition-all shadow-[0_0_30px_rgba(56,189,248,0.35)]">
                  Start Free Trial
                </button>
              </Link>
              <button className="border border-gray-700 text-gray-300 font-medium px-10 py-4 rounded-xl text-lg hover:bg-white/5 transition-all">
                Watch Platform Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── KPI DASHBOARD ROW ── */}
      <section className="py-14 bg-[#111827] border-y border-gray-800">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-xs text-gray-500 uppercase tracking-[0.3em] font-medium mb-8">Live Dashboard — Updated Every Minute</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {kpis.map((k, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-[#0d1117] border border-gray-800 rounded-2xl p-6 hover:border-sky-500/40 transition-all group">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-sky-500/10 text-sky-400 rounded-xl flex items-center justify-center">{k.icon}</div>
                  <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse mt-1"></span>
                </div>
                <p className="text-3xl font-medium text-white mb-1">{k.value}</p>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">{k.label}</p>
                <p className={`text-xs font-medium ${k.status === 'up' ? 'text-sky-400' : k.status === 'down' ? 'text-amber-400' : 'text-gray-500'}`}>{k.delta}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SHIPMENT JOURNEY TIMELINE ── */}
      <section className="py-16 bg-[#0d1117]">
  <div className="max-w-4xl mx-auto px-5">
    <div className="mb-8 text-center">
      <span className="text-sky-400 text-xs font-medium uppercase tracking-widest">LIVE SHIPMENT</span>
      <h2 className="text-3xl font-medium text-white mt-2">Track every order in real-time</h2>
      <p className="text-gray-400 mt-1 text-sm">From warehouse to your doorstep</p>
    </div>

    <div className="bg-[#111827] border border-gray-800 rounded-3xl p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-white font-semibold text-lg">AWB #SHP-20241021-894</p>
          <p className="text-gray-400 text-sm">Mumbai → Pune • Expected Today by 5:00 PM</p>
        </div>

        <div className="flex items-center gap-2 bg-sky-500/10 border border-sky-500/30 text-sky-400 text-xs font-medium px-4 py-2 rounded-2xl">
          <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse"></span>
          IN TRANSIT
        </div>
      </div>

      {/* Compact Timeline */}
      <div className="space-y-5">
        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">✓</div>
            <div className="w-0.5 h-10 bg-gray-700 mt-2"></div>
          </div>
          <div className="flex-1 pt-1">
            <p className="text-white font-medium">Order Shipped</p>
            <p className="text-emerald-400 text-sm">Mumbai Warehouse • 20 May, 10:45 AM</p>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center text-white text-xs font-bold">→</div>
            <div className="w-0.5 h-10 bg-gray-700 mt-2"></div>
          </div>
          <div className="flex-1 pt-1">
            <p className="text-white font-medium">In Transit</p>
            <p className="text-gray-400 text-sm">Out for Delivery • 21 May, 08:30 AM</p>
          </div>
        </div>

        <div className="flex gap-4 opacity-60">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center"></div>
          </div>
          <div className="flex-1 pt-1">
            <p className="text-gray-400 font-medium">Delivered</p>
            <p className="text-gray-500 text-sm">Expected by 5:00 PM Today</p>
          </div>
        </div>
      </div>

      {/* Compact Footer Info */}
      <div className="mt-8 pt-5 border-t border-gray-800 flex justify-between items-center text-xs">
        <div className="text-gray-400">
          Updated just now
        </div>
        <button className="text-sky-400 hover:text-sky-300 font-medium flex items-center gap-1 text-sm">
          View Full Details →
        </button>
      </div>
    </div>
  </div>
</section>

      {/* ── BANNER ── */}
      <section className="relative h-64 overflow-hidden flex items-center justify-center">
        <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80" alt="Logistics Banner" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0d1117]/80"></div>
        <div className="relative z-10 text-center">
          <h2 className="text-4xl font-medium text-white">1,000,000+ <span className="text-sky-400">deliveries</span> tracked and counting.</h2>
          <div className="flex flex-wrap justify-center gap-8 mt-4 text-gray-500 font-medium uppercase tracking-widest text-sm">
            {['FedEx','Blue Dart','DTDC','Delhivery','Ecom Express'].map(c => <span key={c}>{c}</span>)}
          </div>
        </div>
      </section>

      {/* ── CAPABILITY TABS ── */}
      <section className="py-20 bg-[#111827]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-medium text-white mb-3">Built for every layer of your operation.</h2>
            <p className="text-gray-400 text-lg">Select a capability to explore.</p>
          </div>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Pill Nav */}
            <div className="w-full lg:w-64 flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 shrink-0">
              {Object.entries(capTabs).map(([key, val]) => (
                <button key={key} onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-3 px-5 py-4 rounded-2xl font-medium text-sm whitespace-nowrap transition-all ${
                    activeTab === key
                      ? 'bg-sky-500 text-white shadow-[0_0_20px_rgba(56,189,248,0.3)]'
                      : 'bg-[#0d1117] text-gray-400 hover:bg-white/5 border border-gray-800'
                  }`}>
                  <span className="text-xl">{val.icon}</span> {val.label}
                </button>
              ))}
            </div>

            {/* Right Content Panel */}
            <div className="flex-1">
              <AnimatePresence mode="wait">
                <motion.div key={activeTab} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}
                  className="bg-[#0d1117] border border-gray-800 rounded-3xl overflow-hidden">
                  <img src={tab.img} alt={tab.label} className="w-full h-56 object-cover opacity-80" />
                  <div className="p-8">
                    <h3 className="text-2xl font-medium text-white mb-3">{tab.heading}</h3>
                    <p className="text-gray-400 mb-6 leading-relaxed">{tab.body}</p>
                    <div className="flex flex-wrap gap-2">
                      {tab.chips.map((c, i) => (
                        <span key={i} className="text-xs bg-sky-500/10 border border-sky-500/20 text-sky-400 px-3 py-1.5 rounded-full font-medium">{c}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-sky-600 text-center">
        <div className="container mx-auto px-6 max-w-4xl">
          <FiWifi className="text-6xl text-sky-200 mx-auto mb-6" />
          <h2 className="text-5xl font-medium text-white mb-6">Stop flying blind. Start your free trial today.</h2>
          <p className="text-sky-100 text-xl mb-10">Full platform access. No credit card. Cancel anytime.</p>
          <button className="bg-white text-sky-700 font-medium py-4 px-14 rounded-xl text-lg shadow-xl hover:bg-gray-100 transition-all transform hover:-translate-y-1">
            Get Started Free
          </button>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-[#0d1117]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-medium text-white mb-10 text-center">Frequently Asked Questions</h2>
          <Faq faqs={faqs} />
        </div>
      </section>

      <div className="bg-black border-t border-gray-900 text-white"><Footer /></div>
    </div>
  );
}
